import {
  Navigate,
  Outlet,
  useNavigate,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import {
  useInfiniteQuery,
  useQuery,
  type QueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { TIMEOUTS } from '@/constants';
import { cn } from '@/lib/utils';
import { captureException } from '@/lib/errorTracking';
import { useRecordRecentView } from '@/hooks/useRecordRecentView';
import { useStaleWarning } from '@/hooks/useStaleWarning';
import { recentRemoveAction } from '@/hooks/recentRemoveAction';
import {
  ACTIVE_STUDY_STATUSES,
  DP_DETAIL_TABS,
  LIBRARY_HOME_ROUTES,
  type StudyLibrary,
} from '../-data/constants';
import type { DpDetailTab } from '../-data/constants';
import StudyHeader from './StudyHeader';
import { useStudyLibrary } from './hooks/useStudyLibrary';
import { StudyContextProvider } from './study-context';
import { HandCalcGraphsProvider } from '@/hooks/handCalcGraphs';
import { handleGetStudyStudiesStudyIdGetOptions } from '@/api/generated/@tanstack/react-query.gen';
import type {
  GeometryResponse,
  StudyDetailResponse,
} from '@/api/generated/types.gen';
import { CenteredSpinner } from '@/components/CenteredSpinner';
import { listGeometriesInfiniteOptions } from '@/hooks/useDashboardGeometries';
import {
  studyDesignPointsInfiniteOptions,
  useStudyDesignPoints,
} from '@/hooks/useStudyDesignPoints';
import { isChunkLoadError, reloadForNewBuild } from '@/utils/chunkLoadError';
import { apiErrorMessage } from '@/utils/apiErrorMessage';
import {
  READ_ONLY_BLOCKED_PHASES,
  httpStatus,
  isAccessDeniedError,
  isBelowEditorRole,
} from '@/utils/shareAccess';

/**
 * The study detail search params. Exported because BOTH route files that mount
 * this layout validate with it — `/simops/$studyId` and `/evaltool/$studyId` —
 * and a param declared on one of them only would be stripped on the other.
 */
export const studyDetailSearchSchema = z.object({
  dp: z.string().optional().catch(undefined),
  tab: z.enum(DP_DETAIL_TABS).optional().catch(undefined),
  // Set only on links from the "Shared with me" tab (ENG-2009), so a 404 there
  // reads as "the owner stopped sharing this link" rather than the generic
  // doesn't-exist-or-no-access copy.
  from: z.literal('recent').optional().catch(undefined),
  // The Compare picks. Declared HERE and only here: the study layout validates
  // search first and strips anything it does not name, so a child route
  // cannot add a param of its own — and ENG-1917 means the bare-URL
  // `/simops/$studyId` mounts the Results surface too, not just `/results`.
  //
  // "A string" here, deduped and capped where it is read
  // (`parseComparePicks`), because a URL a person typed should degrade to the
  // best available comparison rather than be thrown away whole.
  cmp: z.string().optional().catch(undefined),
});

// Shared by the route loader and the component's useQuery so both hit the
// same cache entry with the same retry policy (fail fast to the
// not-found UI rather than retrying a 404 study).
const studyQueryOptions = (studyId: string) => ({
  ...handleGetStudyStudiesStudyIdGetOptions({ path: { study_id: studyId } }),
  retry: false,
});

export type StudyDetailSearch = z.infer<typeof studyDetailSearchSchema>;

/**
 * Warm the study + first page of design points/geometries so navigation lands
 * on content instead of a spinner. Best-effort: components keep their
 * useQuery/useInfiniteQuery and own pagination loops, polling, retries, and
 * error UI — a failed warm just falls through to those.
 *
 * Shared by both study detail routes for the reason the schema is: the two
 * mount the same component, and a warm that ran under one prefix only would
 * make `/evaltool/<id>` slower than `/simops/<id>` for no reason a reader
 * could see. Returns the study so a caller can decide on it without a second
 * cache read; `undefined` when it failed to load, which the component's own
 * not-found and load-failed cards handle.
 */
export async function studyDetailLoader({
  queryClient,
  studyId,
}: {
  queryClient: QueryClient;
  studyId: string;
}): Promise<StudyDetailResponse | undefined> {
  const studyPromise = queryClient.ensureQueryData(studyQueryOptions(studyId));
  const geometriesPromise = queryClient.ensureInfiniteQueryData(
    listGeometriesInfiniteOptions(),
  );

  const study = await studyPromise.catch(() => undefined);
  // Mirror the component's gate: design points exist once out of draft.
  const pointsPromise =
    study && study.status !== 'draft'
      ? queryClient.ensureInfiniteQueryData(
          studyDesignPointsInfiniteOptions(studyId),
        )
      : undefined;

  await Promise.all([
    geometriesPromise.catch(() => undefined),
    pointsPromise?.catch(() => undefined),
  ]);
  // Handed back rather than discarded: `/evaltool/$studyId` decides on the
  // status and would otherwise re-`ensureQueryData` the entry this already
  // awaited, spelling the fetch-and-swallow policy a second time.
  return study;
}

/** The centered icon-title-body-actions card every study error state uses. */
function NoticeCard({
  icon,
  iconClassName,
  title,
  children,
}: {
  icon: string;
  iconClassName: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-1 items-center justify-center bg-muted">
      <div className="max-w-md px-6 text-center">
        <div
          className={cn(
            'mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full',
            iconClassName,
          )}
        >
          <span className="text-3xl">{icon}</span>
        </div>
        <h2 className="mb-2 text-xl font-semibold text-foreground">{title}</h2>
        {children}
      </div>
    </div>
  );
}

const PRIMARY_BUTTON =
  'rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90';

function StudyNotFound({
  message,
  library,
  secondaryAction,
}: {
  message: string;
  /** Which library's landing the primary button returns to. */
  library: StudyLibrary;
  /** Optional second button — the "Shared with me" tab's "remove this row". */
  secondaryAction?: { label: string; onClick: () => void };
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <NoticeCard
      icon="!"
      iconClassName="bg-muted text-muted-foreground"
      title={t('simopsRoutes.notFound.title')}
    >
      <p className="mb-6 text-muted-foreground">{message}</p>
      <div className="flex items-center justify-center gap-2">
        <button
          type="button"
          onClick={() => navigate({ to: LIBRARY_HOME_ROUTES[library] })}
          className={PRIMARY_BUTTON}
        >
          {library === 'evaltool'
            ? t('simopsShared.studyHeader.backToEvaluations')
            : t('simopsRoutes.notFound.goToStudies')}
        </button>
        {secondaryAction && (
          <button
            type="button"
            onClick={secondaryAction.onClick}
            className="rounded-lg border border-border bg-card px-4 py-2 text-sm text-foreground hover:bg-muted"
          >
            {secondaryAction.label}
          </button>
        )}
      </div>
    </NoticeCard>
  );
}

/**
 * 404 (the backend's answer for both missing and no-access), 403, or a signed-out
 * session. Anything else is a failure on our side, not a missing study.
 */
function isStudyMissing(error: unknown): boolean {
  return isAccessDeniedError(error) || httpStatus(error) === 403;
}

/** A crash or a failed load — the study is fine, so offer a reload, not a way out. */
function PageErrorNotice({ error }: { error: unknown }) {
  const { t } = useTranslation();
  const message = apiErrorMessage(error);

  return (
    <NoticeCard
      icon="!"
      iconClassName="bg-muted text-muted-foreground"
      title={t('simopsRoutes.pageError.title')}
    >
      <p className="mb-4 text-muted-foreground">
        {t('simopsRoutes.pageError.body')}
      </p>
      {message && (
        <code className="mb-6 block rounded bg-card px-3 py-2 text-left text-xs break-words text-muted-foreground select-all">
          {message}
        </code>
      )}
      <button
        type="button"
        onClick={() => reloadForNewBuild()}
        className={PRIMARY_BUTTON}
      >
        {t('simopsRoutes.staleBundle.reload')}
      </button>
    </NoticeCard>
  );
}

function StaleBundleNotice() {
  const { t } = useTranslation();

  return (
    <NoticeCard
      icon="↻"
      iconClassName="bg-info/15 text-info-strong"
      title={t('simopsRoutes.staleBundle.title')}
    >
      <p className="mb-6 text-muted-foreground">
        {t('simopsRoutes.staleBundle.body')}
      </p>
      <button
        type="button"
        onClick={() => reloadForNewBuild()}
        className={PRIMARY_BUTTON}
      >
        {t('simopsRoutes.staleBundle.reload')}
      </button>
    </NoticeCard>
  );
}

export function StudyErrorBoundary({ error }: ErrorComponentProps) {
  const { t } = useTranslation();
  // The boundary is mounted by both study routes and takes no props of its
  // own, so it reads the library off the path the way `-components/**` does.
  const library = useStudyLibrary();
  const chunkLoad = isChunkLoadError(error);
  const missing = isStudyMissing(error);

  // Reported here, not in `PageErrorNotice`: the in-page load-failure card
  // re-renders with a fresh error on every failed poll.
  useEffect(() => {
    if (chunkLoad || missing) return;
    // A non-Error API body becomes an `Error` carrying its detail inside the
    // wrapper, with `status`/`detail` as event properties.
    captureException(error, { area: 'study-page' });
  }, [error, chunkLoad, missing]);

  // Chunk-load failures look like "Study not found" to users today because
  // any thrown error during render lands here. Disambiguate: stale-bundle
  // errors get their own card with a Reload CTA so the user knows the
  // study is fine and the cure is a refresh, not a navigate-away.
  if (chunkLoad) {
    return <StaleBundleNotice />;
  }

  // Only a missing study says "Study not found" (ENG-2720).
  if (missing) {
    return (
      <StudyNotFound
        library={library}
        message={t('simopsRoutes.notFound.noAccess')}
      />
    );
  }

  return <PageErrorNotice error={error} />;
}

/**
 * The study detail shell — data loading, the not-found / stale-bundle guards,
 * the study header, and the `<Outlet />` its step routes render into.
 *
 * A component rather than a route module because it is mounted TWICE, once per
 * product library: `/simops/$studyId` and `/evaltool/$studyId` (ENG-1917's
 * `?origin=evaltool` used to fake the second one from inside the first). The
 * two route files own the router-facing half — search validation, loader,
 * error component — and hand this the studyId and the validated search.
 *
 * Which library is live it reads off the path, like every other consumer and
 * like `StudyErrorBoundary` above: a prop would be a second way to answer a
 * question the URL already answers, and the two could disagree.
 */
export function StudyDetailLayout({
  studyId,
  search,
}: {
  studyId: string;
  search: StudyDetailSearch;
}) {
  const { t } = useTranslation();
  const library = useStudyLibrary();
  const { dp: selectedDpId, tab: detailTab, from } = search;
  const navigate = useNavigate();

  const setSelectedDp = useCallback(
    (next: { dp?: string; tab?: DpDetailTab } | null) => {
      void navigate({
        to: '.',
        search: (prev) => {
          if (next === null) return { ...prev, dp: undefined, tab: undefined };
          return { ...prev, ...next };
        },
        replace: true,
      });
    },
    [navigate],
  );

  const studyQuery = useQuery({
    ...studyQueryOptions(studyId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && ACTIVE_STUDY_STATUSES.has(status)
        ? TIMEOUTS.STUDY_POLL_INTERVAL
        : false;
    },
  });
  const isActive =
    studyQuery.data?.status != null &&
    ACTIVE_STUDY_STATUSES.has(studyQuery.data.status);

  const recentIdentity = useRecordRecentView({
    kind: 'study',
    id: studyId,
    name: studyQuery.data?.name,
    status: studyQuery.data?.status,
    myRole: studyQuery.data?.my_role,
  });

  // Staleness is judged by last activity (`updated_at`), not creation date, so
  // a long-lived study that was run or edited recently doesn't warn. Suppressed
  // entirely while the study is actively running — an in-flight study is by
  // definition being processed by the current pipeline.
  useStaleWarning(
    isActive ? undefined : studyQuery.data?.updated_at,
    t('compCommon.staleWarning.messageStudy'),
    {
      label: t('compCommon.staleWarning.actionCreateStudy'),
      // `/simops/new` for an EvalTool reader too, unlike the two navigations
      // above and below: creating a study is a SimOps action, and EvalTool has
      // no equivalent to send them to.
      onAction: () => void navigate({ to: '/simops/new' }),
    },
  );

  const { items: designPoints } = useStudyDesignPoints(studyId, {
    enabled: studyQuery.data != null && studyQuery.data.status !== 'draft',
    isActive,
  });

  // Match the MeshGen dashboard's pagination — the bare `useQuery` call
  // here was reading only the first page (~25-50 items), which caused the
  // SimOps mesh picker to silently hide older geometries when the user's
  // history exceeded the page size. Walk all pages via useInfiniteQuery
  // + cursor so the picker mirrors the dashboard.
  const geometriesQuery = useInfiniteQuery({
    ...listGeometriesInfiniteOptions(),
    enabled: studyQuery.data != null,
  });

  // Auto-fetch the next page until the cursor runs out — the picker
  // expects the full list in one go (no virtualization), and the user's
  // first interaction is to pick a file, not to scroll a paginator.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = geometriesQuery;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const geometries = useMemo<GeometryResponse[]>(
    () => (geometriesQuery.data?.pages ?? []).flatMap((page) => page.items),
    [geometriesQuery.data],
  );

  if (studyQuery.isLoading) {
    return <CenteredSpinner />;
  }

  if (studyQuery.isError) {
    // By status: a 404/403 arrives as the parsed error body, not an `Error`.
    // Any other failure (5xx, network) is ours, not a missing study.
    if (!isStudyMissing(studyQuery.error)) {
      return <PageErrorNotice error={studyQuery.error} />;
    }
    // Arrived from the "Shared with me" tab: this study opened once, so a 404
    // now reads as the owner revoking the link rather than a bad id.
    if (from === 'recent') {
      return (
        <StudyNotFound
          library={library}
          message={t('simopsRoutes.notFound.shareRevoked')}
          secondaryAction={recentRemoveAction(
            recentIdentity,
            'study',
            studyId,
            t('compProductLibrary.recentlyViewed.remove'),
            () => void navigate({ to: LIBRARY_HOME_ROUTES[library] }),
          )}
        />
      );
    }
    return (
      <StudyNotFound
        library={library}
        message={t('simopsRoutes.notFound.noAccess')}
      />
    );
  }

  if (!studyQuery.data) {
    return (
      <StudyNotFound
        library={library}
        message={t('simopsRoutes.notFound.couldNotLoad')}
      />
    );
  }

  const study = studyQuery.data;

  // ENG-1068 originally bounced viewer/commenter to `/share/study/` — a
  // wholesale redirect that made sense when the results surface had no
  // per-button role gates. Now every mutating action on the running /
  // review / completed phases reads `canEditStudy` and drops out for
  // read-only tiers, so the same phase views (with ActivityBar sidebar
  // back to their own dashboards) are safe to render for viewer +
  // commenter. `READ_ONLY_BLOCKED_PHASES` lists the phases that still
  // carry study-wide authoring the read-only tier can't act on (`draft`);
  // the share auto-forward mirrors the same list so the two guards can't
  // drift into a redirect loop.
  if (
    isBelowEditorRole(study.my_role) &&
    READ_ONLY_BLOCKED_PHASES.has(study.status)
  ) {
    return <Navigate to="/share/study/$studyId" params={{ studyId }} replace />;
  }

  return (
    <div className="h-full flex-1 overflow-y-auto overscroll-none">
      <StudyContextProvider
        value={{
          study,
          designPoints,
          geometries,
          selectedDpId,
          detailTab,
          onSelectDp: setSelectedDp,
        }}
      >
        {/* One set of hand-calc observers for the whole study: every surface
            below reads this context rather than mounting its own queries. */}
        <HandCalcGraphsProvider studyId={study.id} designPoints={designPoints}>
          <div className="flex h-full flex-col">
            <StudyHeader
              study={study}
              designPoints={designPoints}
              library={library}
            />
            <div className="flex-1 overflow-hidden">
              <Outlet />
            </div>
          </div>
        </HandCalcGraphsProvider>
      </StudyContextProvider>
    </div>
  );
}
